import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SystemService from '../../services/SystemService';
import ProjectService from '../../services/ProjectService';
import { CreateProjectRequest, CreateSystemRequest } from '../../models/Api';
import Constants from '../../utils/Constants';

export interface BaseShortcutProps {
  projectNameKey: string;
  systemNameKey: string;
  systemTypeUniqueId: string;
  autoExecAction: string;
}

export interface ShowAddPartDialogProps extends BaseShortcutProps {
  addPartDialogSelection: string;
}

export interface LaunchCreatedPartSizingToolProps extends BaseShortcutProps {
  groupUniqueName: string;
  partCategoryName: string;
}

export type ShortcutProps = ShowAddPartDialogProps | LaunchCreatedPartSizingToolProps;

export function NewPdnShortcut(props: ShortcutProps) {
  const { projectNameKey, systemNameKey, systemTypeUniqueId, autoExecAction } = props;

  const { t } = useTranslation();
  const [statusMessageKey, setStatusMessageKey] = useState('');
  const navigate = useNavigate();

  async function createProjectAndSystem() {
    setStatusMessageKey('CreatingNewProject');
    const newProject = { name: t(projectNameKey) } as CreateProjectRequest;
    const createProjectResponse = await ProjectService.create(newProject);
    if (createProjectResponse.status != 200) throw new Error('Error while creating new project');
    const projectId = createProjectResponse.data.id;
    setStatusMessageKey('FetchingSystemTemplates');
    const systemTypes = await SystemService.getSystemTypesAndTemplates();
    if (!systemTypes) throw new Error('Error while fetching system types and templates');
    const systemType = systemTypes.find((_systemType) => _systemType.uniqueId === systemTypeUniqueId);
    if (!systemType) throw new Error(`${systemTypeUniqueId} was not found in the list of system types`);
    const templateId = systemType.templates?.at(0)?.id;
    if (!templateId) throw new Error(`List of templates for system type with ID ${systemType.id} is empty`);
    setStatusMessageKey('CreatingNewSystem');
    const newSystem = {
      projectId,
      name: t(systemNameKey),
      templateId,
    } as CreateSystemRequest;
    const createSystemResponse = await SystemService.createSystem(newSystem);
    if (createSystemResponse.status != 200) throw new Error('Error while creating new system');
    return createSystemResponse.data;
  }

  useEffect(() => {
    SystemService.clearSystemData();
    const main = async () => {
      const newSystemId = await createProjectAndSystem();
      const newSystemRoute = `/${Constants.routes.systems}/${newSystemId}`;

      sessionStorage.setItem(Constants.sessionStorageKeys.autoExecAction, autoExecAction);

      if (autoExecAction === Constants.autoExecActions.launchSizingTool) {
        const { groupUniqueName, partCategoryName } = props as LaunchCreatedPartSizingToolProps;
        sessionStorage.setItem(Constants.sessionStorageKeys.autoCreateGroupUniqueName, groupUniqueName);
        sessionStorage.setItem(Constants.sessionStorageKeys.autoCreatePartCategoryName, partCategoryName);
      }
      if (autoExecAction === Constants.autoExecActions.showAddPartDialog) {
        const { addPartDialogSelection } = props as ShowAddPartDialogProps;
        sessionStorage.setItem(Constants.sessionStorageKeys.addPartDialogSelection, addPartDialogSelection);
      }

      setStatusMessageKey('RedirectingToNewSystem');
      navigate(newSystemRoute, { replace: true });
    };

    main();
  }, []);

  return <h1>{t(statusMessageKey)}</h1>;
}
